// filter.pipe.ts
import { Pipe, PipeTransform } from '@angular/core';

export function escapeSearchRegex(value: string): string {
  return value.replace(/[|\\{}()[\]^$+.-]/g, "\\$&");
}

export function buildSearchPattern(term: string): RegExp | null {
  const trimmedTerm = term.trim();
  if (!trimmedTerm)
    return null;

  const hasAsterisk = trimmedTerm.indexOf("*") >= 0;
  const startsWithAsterisk = trimmedTerm.startsWith("*");
  const endsWithAsterisk = trimmedTerm.endsWith("*");
  const pattern = escapeSearchRegex(trimmedTerm)
    .replace(/\*/g, ".*")
    .replace(/\?/g, ".");

  if (!hasAsterisk)
    return new RegExp(pattern, "i");

  const prefix = startsWithAsterisk ? "" : "^";
  const suffix = endsWithAsterisk ? "" : "$";
  return new RegExp(`${prefix}${pattern}${suffix}`, "i");
}

export function matchesSearchRule(value: string | undefined | null, term: string): boolean {
  const normalizedValue = value ?? "";
  const parts = term
    .split(",")
    .map((part: string) => part.trim())
    .filter((part: string) => part !== "");

  if (parts.length === 0)
    return true;

  const includeParts = parts.filter((part: string) => part[0] !== "!");
  const excludeParts = parts
    .filter((part: string) => part[0] === "!")
    .map((part: string) => part.substring(1).trim())
    .filter((part: string) => part !== "");

  for (let i = 0; i < excludeParts.length; i++) {
    const excludePattern = buildSearchPattern(excludeParts[i]);
    if (excludePattern?.test(normalizedValue))
      return false;
  }

  if (includeParts.length === 0)
    return true;

  for (let i = 0; i < includeParts.length; i++) {
    const includePattern = buildSearchPattern(includeParts[i]);
    if (includePattern?.test(normalizedValue))
      return true;
  }

  return false;
}

export function matchesAnySearchRule(term: string, ...values: Array<string | undefined | null>): boolean {
  for (let i = 0; i < values.length; i++) {
    if (matchesSearchRule(values[i], term))
      return true;
  }

  return false;
}

@Pipe({
    name: 'filter',
    pure: false // recalc when items/searchText change
    ,
    standalone: false
})
export class FilterPipe implements PipeTransform {
  transform<T extends Record<string, any>>(
    items: T[],
    searchText: string,
    prop?: keyof T
  ): T[] {
    if (!items) return [];
    if (!searchText) return items;

    return items.filter(item => {
      if (prop) {
        return matchesSearchRule(((item[prop] ?? '') as string), searchText);
      }
      // check all properties
      return Object.values(item).some(val =>
        val && matchesSearchRule(val.toString(), searchText)
      );
    });
  }
}
